import { useEffect, useState, type FormEvent } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Alert } from '../atoms/Alert'
import { Button } from '../atoms/Button'
import { Input } from '../atoms/Input'
import { PASSWORD } from '../../../constants/password.constants'

/**
 * Change-password popup — a small centered modal (NOT the slide-in
 * overlay). Old + new + confirm, gated on (new >= MINLENGTH, new ===
 * confirm). onSubmit performs the change and rejects with the server
 * message on failure; on success the modal shows a brief confirmation
 * then closes. Esc / dim / Cancel closes it.
 */
export function ChangePasswordModal({
  onClose,
  onSubmit
}: {
  readonly onClose: () => void
  readonly onSubmit: (current: string, next: string, confirm: string) => Promise<void>
}): JSX.Element {
  const reduce = useReducedMotion() === true
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const lengthOk = next.length >= PASSWORD.MINLENGTH
  const matches = next === confirm && confirm.length > 0
  const differs = next !== current
  const canSubmit = current.length > 0 && lengthOk && matches && differs && !busy

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await onSubmit(current, next, confirm)
      setDone(true)
      window.setTimeout(onClose, 1100)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Change password"
      onClick={onClose}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="relative w-full max-w-sm rounded-2xl border border-border bg-bg-primary p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        variants={{ hidden: { scale: reduce ? 1 : 0.96, opacity: 0 }, visible: { scale: 1, opacity: 1 } }}
        transition={{ duration: reduce ? 0.15 : 0.25, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="font-heading text-h3 text-charcoal mb-4">Change password</h2>

        {done ? (
          <Alert tone="success">Password updated.</Alert>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" noValidate>
            {error !== null ? <Alert tone="error">{error}</Alert> : null}
            <label className="block">
              <span className="font-body text-body-sm text-text-secondary block mb-1">Current password</span>
              <Input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                disabled={busy}
                required
              />
            </label>
            <label className="block">
              <span className="font-body text-body-sm text-text-secondary block mb-1">
                New password ({PASSWORD.MINLENGTH}+ characters)
              </span>
              <Input
                type="password"
                autoComplete="new-password"
                minLength={PASSWORD.MINLENGTH}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                disabled={busy}
                required
              />
            </label>
            <label className="block">
              <span className="font-body text-body-sm text-text-secondary block mb-1">Confirm new password</span>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                hasError={confirm.length > 0 && !matches}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
                required
              />
              {confirm.length > 0 && !matches ? (
                <span className="block text-body-xs font-body text-terra mt-1">Passwords don&rsquo;t match.</span>
              ) : null}
            </label>
            <div className="flex gap-2 pt-1">
              <Button type="submit" variant="primary" size="lg" disabled={!canSubmit} className="flex-1">
                {busy ? 'Saving…' : 'Update password'}
              </Button>
              <Button type="button" variant="secondary" size="lg" onClick={onClose} disabled={busy} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  )
}
